/**
 * 계좌 관리 Pinia Store
 * 계좌 목록, 선택된 계좌, WebSocket 토큰을 관리합니다.
 *
 * 주의: 이 Store는 앱별 서비스(LocalStorageService, getWebSocketService)에 의존합니다.
 * 앱별로 이 서비스들을 제공해야 합니다.
 */
import { useOrderDataStore } from '../order/useOrderDataStore';
import { useAssetStore } from '../asset/useAssetStore';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
/**
 * 계좌별 데이터 초기화
 * 계좌 변경 시 이전 계좌의 데이터를 초기화합니다.
 *
 * @public
 */
export const resetAccountSpecificData = () => {
    try {
        // AssetStore 초기화 (자산 데이터)
        const assetStore = useAssetStore();
        assetStore.resetAssetData();
        // OrderDataStore 초기화 (주문 데이터)
        const orderDataStore = useOrderDataStore();
        orderDataStore.clearAllData();
    }
    catch (error) {
        console.error('[resetAccountSpecificData] 계좌별 데이터 초기화 실패:', error);
    }
};
// 전역 서비스 인스턴스 (앱별로 설정)
let accountStoreServices = null;
/**
 * AccountStore에 필요한 서비스를 설정합니다.
 * 앱별로 앱 초기화 시 호출해야 합니다.
 */
export function setAccountStoreServices(services) {
    accountStoreServices = services;
}
/**
 * AccountStore에 필요한 서비스를 가져옵니다.
 */
function getAccountStoreServices() {
    if (!accountStoreServices) {
        throw new Error('AccountStore services not initialized. Call setAccountStoreServices() in app initialization.');
    }
    return accountStoreServices;
}
export const useAccountStore = defineStore('account', () => {
    const accountList = ref([]);
    // 서비스 가져오기 (지연 평가)
    const getServices = () => getAccountStoreServices();
    // undefined로 초기화
    // selectedAccount는 initialize()에서 비로그인 상태를 체크한 후 설정
    const selectedAccount = ref(undefined);
    // 계좌 인증용 JWT 토큰
    // WebSocket private 채널 구독 및 API 요청의 X-Account-Token 헤더에 사용
    const accountToken = ref(null);
    const accountSimpleAuthEnabled = ref(false);
    // 초기화 완료 상태 (내부 상태)
    const _isInitialized = ref(false);
    /**
     * 계좌 비밀번호 재설정 대상 계좌번호
     * 비밀번호 오류 횟수 초과 등으로 별도의 계좌번호를 기억해야 할 때 사용
     */
    const resetChangeAccountNoData = ref(null);
    /**
     * 계좌 비밀번호 실패 횟수
     */
    const accountPasswordFailedCountData = ref(0);
    /**
     * 계좌 인증 진행 상태
     * 계좌 인증 토큰 발급 중인지 여부를 나타냅니다.
     */
    const isAuthenticating = ref(false);
    /**
     * 계좌 인증 완료를 기다리는 Promise
     * 계좌 인증이 진행 중일 때 다른 API 요청이 대기할 수 있도록 합니다.
     */
    let authenticationPromise = null;
    let authenticationResolve = null;
    /**
     * 계좌 인증 시작
     * 계좌 인증이 진행 중임을 표시하고, 대기 중인 요청들이 기다릴 수 있는 Promise를 반환합니다.
     * @returns 계좌 인증 완료를 기다리는 Promise
     */
    const startAccountAuthentication = () => {
        if (!isAuthenticating.value) {
            isAuthenticating.value = true;
            authenticationPromise = new Promise((resolve) => {
                authenticationResolve = resolve;
            });
        }
        return authenticationPromise;
    };
    /**
     * 계좌 인증 완료
     * 계좌 인증이 완료되었음을 표시하고, 대기 중인 요청들이 계속 진행할 수 있도록 Promise를 resolve합니다.
     *
     * authenticationPromise를 await하고 있던 요청들(interceptor의 accountTokenResolver 등)이
     * 이제 계속 진행할 수 있게 됩니다.
     */
    const completeAccountAuthentication = () => {
        isAuthenticating.value = false;
        if (authenticationResolve) {
            authenticationResolve(); // Promise를 resolve하여 대기 중인 요청들이 계속 진행할 수 있도록 함
            authenticationResolve = null;
            authenticationPromise = null;
        }
    };
    /**
     * 초기화 완료 상태 확인 (computed)
     * @returns 초기화 완료 여부
     */
    const isInitialized = computed(() => _isInitialized.value);
    /**
     * 계좌 인증용 JWT 토큰을 설정합니다.
     * 계좌 선택 시 호출됩니다.
     * WebSocket private 채널 구독 및 API 요청의 X-Account-Token 헤더에 사용됩니다.
     */
    const setAccountToken = (token) => {
        accountToken.value = token;
        // localStorage에 저장 (Access Token과 동일한 방식)
        const services = getServices();
        services.setLocalStorageItem(services.LocalStorageKey.ACCOUNT_TOKEN, token);
        // WebSocket 서비스에 토큰 전달
        const wsService = services.getWebSocketService();
        wsService.setAccessToken(token);
    };
    /**
     * 계좌 인증용 JWT 토큰을 제거합니다.
     * 계좌 해제 또는 로그아웃 시 호출됩니다.
     */
    const clearAccountToken = () => {
        accountToken.value = null;
        // localStorage에서 제거
        const services = getServices();
        services.removeLocalStorageItem(services.LocalStorageKey.ACCOUNT_TOKEN);
        // WebSocket 서비스의 토큰도 제거
        const wsService = services.getWebSocketService();
        wsService.clearAccessToken();
    };
    /**
     * 계좌 등급 변경 시 Store 초기화 및 이벤트 발생
     * 계좌 등급이 변경되면 종목 메타데이터와 시장 데이터를 재로드해야 합니다.
     *
     * @param params - 계좌 등급 변경 정보
     * @param params.previousGrade - 이전 계좌 등급
     * @param params.currentGrade - 현재 계좌 등급
     * @param params.accountNo - 계좌번호
     * @param params.previousAccountNo - 이전 계좌번호 (재연결 시에는 accountNo와 동일)
     */
    const handleAccountGradeChanged = async (params) => {
        const { previousGrade, currentGrade, accountNo, previousAccountNo } = params;
        try {
            // MarketDataStore 초기화 (시장 데이터)
            // 주의: clearAllMarketData()는 차트 종목을 초기화하지 않음
            // 차트 종목은 유지하고 시세 데이터만 초기화
            const { useMarketDataStore } = await import('../market/useMarketDataStore');
            const marketDataStore = useMarketDataStore();
            marketDataStore.clearAllMarketData();
            // isInitialized를 false로 설정하여 재초기화 가능하도록 함
            marketDataStore.isInitialized = false;
            // MetadataStore 초기화 (종목 메타데이터)
            const { useMetadataStore } = await import('../metadata/useMetadataStore');
            const metadataStore = useMetadataStore();
            metadataStore.resetMetadata();
            // 이벤트 발생 (selectedAccountNo가 이미 업데이트된 상태)
            // ref의 값 할당은 동기적으로 즉시 반영되므로 setTimeout 없이도 순서가 보장됨
            // selectedAccount.value = accountNo;가 이미 실행되었으므로
            // 이벤트 리스너에서 accountStore.selectedAccountNo를 확인할 때 이미 업데이트된 값을 사용할 수 있음
            // 로그 출력은 이벤트 리스너(useSymbolData의 accountGradeChangedListener)에서 처리 완료 후 수행됨
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('accountGradeChanged', {
                    detail: {
                        previousGrade,
                        currentGrade,
                        accountNo,
                        previousAccountNo,
                    },
                }));
            }
            else {
                console.error('[AccountStore] window가 없어 이벤트를 발생시킬 수 없습니다.');
            }
        }
        catch (error) {
            console.error('[AccountStore] 계좌 등급 변경 시 초기화 실패:', error);
        }
    };
    /**
     * 선택된 계좌를 변경하고 localStorage에 저장합니다.
     * WebSocket JWT 토큰을 발급받아 저장합니다.
     * Private 채널 구독은 주문 페이지의 useWebSocketSubscription에서 관리합니다.
     *
     * @param accountNo - 선택할 계좌번호
     */
    const selectAccount = async (accountNo, isAccountAuthorized = false) => {
        // 이전 계좌와 동일하고 토큰이 이미 있으면 스킵
        if (selectedAccount.value === accountNo && accountToken.value) {
            return;
        }
        const services = getServices();
        const previousAccountNo = selectedAccount.value;
        if (accountNo) {
            // 이전 계좌와 현재 계좌의 등급 비교
            const previousAccount = previousAccountNo
                ? accountList.value.find((acc) => acc.accountNo === previousAccountNo)
                : null;
            const currentAccount = accountList.value.find((acc) => acc.accountNo === accountNo);
            const previousGrade = previousAccount?.accountGrade;
            const currentGrade = currentAccount?.accountGrade;
            const isGradeChanged = previousAccountNo && previousGrade && currentGrade && previousGrade !== currentGrade;
            // 계좌가 실제로 변경되는 경우에만 이전 토큰으로 구독 해제
            // 새 토큰 발급 전에 이전 토큰을 제거하지 않고, 새 토큰 발급 후에 처리
            const isAccountChanged = previousAccountNo && previousAccountNo !== accountNo;
            // 계좌가 변경되는 경우: 이전 토큰 제거 전에 새 토큰을 먼저 발급받음
            // 이렇게 하면 watch가 트리거될 때 새 토큰이 이미 설정되어 있음
            if (isAccountChanged) {
                // 이전 토큰 제거 (WebSocket 서비스에서만 제거, accountToken은 유지)
                // 새 토큰 발급 후에 accountToken을 업데이트하므로
                // watch가 트리거될 때 새 토큰이 이미 설정되어 있음
                const wsService = services.getWebSocketService();
                wsService.clearAccessToken();
            }
            // 계좌 인증 시작
            // 다른 API 요청들 대기
            startAccountAuthentication();
            // WebSocket JWT 토큰 발급 (private 채널 구독용)
            // 계좌 업데이트 전에 토큰을 먼저 발급받아야 watch가 트리거될 때 새 토큰이 준비되어 있음
            let token;
            try {
                if (!isAccountAuthorized) {
                    const response = await services.authService.getAccountJwtToken({ accountNo });
                    if (response.data?.token) {
                        token = response.data.token;
                        services.setLocalStorageItem(services.LocalStorageKey.ACCOUNT_TOKEN, token);
                    }
                    else {
                        console.error('[AccountStore] JWT 토큰 발급 실패: 응답에 토큰 없음');
                    }
                }
                else {
                    token = services.getLocalStorageItem(services.LocalStorageKey.ACCOUNT_TOKEN) || undefined;
                }
            }
            catch (error) {
                console.error('[AccountStore] JWT 토큰 발급 실패:', error);
            }
            finally {
                // 계좌 인증 완료, 대기 중인 요청들 해제
                completeAccountAuthentication();
            }
            // 새 토큰을 먼저 설정 (계좌 업데이트 전에)
            // 이렇게 하면 watch가 트리거될 때 새 토큰이 이미 설정되어 있음
            if (token) {
                // 계좌가 변경되는 경우: 이전 토큰을 완전히 제거하고 새 토큰 설정
                if (isAccountChanged) {
                    // localStorage에서 이전 토큰 제거
                    services.removeLocalStorageItem(services.LocalStorageKey.ACCOUNT_TOKEN);
                    // accountToken ref 초기화
                    accountToken.value = null;
                }
                // 새 토큰 설정 (WebSocket 서비스에도 자동으로 설정됨)
                setAccountToken(token);
            }
            else {
                // 토큰 발급 실패 시 이전 토큰 제거
                if (isAccountChanged) {
                    clearAccountToken();
                }
            }
            // 중요: 이벤트 발생 전에 selectedAccountNo를 먼저 업데이트해야 함
            // 이벤트 리스너에서 accountStore.selectedAccountNo를 확인하므로
            // 이벤트 발생 시점에는 이미 업데이트되어 있어야 함
            // 토큰이 먼저 설정된 후에 계좌를 업데이트하므로
            // watch가 트리거될 때 새 토큰이 이미 준비되어 있음
            selectedAccount.value = accountNo;
            resetAccountPasswordFailedCount();
            // 빠른거래 상태 복원:
            // - 저장된 빠른거래 동의 값이 있고, 계좌가 실제로 변경되지 않은 경우에만 유지
            const savedQuickTradeAgreed = services.getLocalStorageItem(services.LocalStorageKey.SAVE_ACCOUNT_PASSWORD_AGREED);
            const isQuickTradeAgreed = savedQuickTradeAgreed === 'true';
            setAccountSimpleAuthEnabled(!isAccountChanged && isQuickTradeAgreed);
            services.setLocalStorageItem(services.LocalStorageKey.SELECTED_ACCOUNT, accountNo);
            // 계좌가 실제로 변경된 경우
            if (isAccountChanged) {
                // 계좌별 데이터 초기화 (이전 계좌의 데이터 제거)
                resetAccountSpecificData();
                // 계좌 등급이 변경된 경우 추가 초기화 및 데이터 재로드 필요
                if (isGradeChanged) {
                    await handleAccountGradeChanged({
                        previousGrade,
                        currentGrade,
                        accountNo,
                        previousAccountNo,
                    });
                }
                else {
                    // 계좌 등급이 변경되지 않은 경우 (계좌만 변경)
                    // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
                    // 계좌 변경 시 시세 정보 로그 출력
                    try {
                        const services = getServices();
                        const wsService = services.getWebSocketService();
                        // logQuoteData가 public 메서드이므로 호출 가능
                        if (typeof wsService.logQuoteData === 'function') {
                            wsService.logQuoteData('accountChange');
                        }
                    }
                    catch (error) {
                        // WebSocketService를 사용할 수 없는 경우 무시 (정상적인 상황일 수 있음)
                        // console.error('[AccountStore] WebSocketService 로그 출력 실패:', error);
                    }
                }
            }
        }
        else {
            // 계좌 해제 (다른 클라이언트에서 비활성화한 경우 등)
            // 로그아웃 시와 동일하게 계좌별 데이터 초기화
            resetAccountSpecificData();
            selectedAccount.value = undefined;
            services.removeLocalStorageItem(services.LocalStorageKey.SELECTED_ACCOUNT);
            clearAccountToken();
        }
    };
    /**
     * 계좌 목록을 설정합니다.
     * 로컬 스토리지에 저장된 계좌가 있으면 해당 계좌를 확인하고,
     * 목록에 있으면 선택하고, 없으면 첫 번째 활성 계좌(visible === 'Y')를 선택합니다.
     * 로컬 스토리지에 저장된 계좌가 없으면 첫 번째 활성 계좌를 선택합니다.
     * 활성 계좌가 없는 경우에는 선택하지 않습니다.
     *
     * @param list - 설정할 계좌 목록
     */
    const setAccountList = async (list) => {
        accountList.value = list;
        const services = getServices();
        // 로컬 스토리지에서 저장된 계좌 확인
        const storedAccountNo = services.getLocalStorageItem(services.LocalStorageKey.SELECTED_ACCOUNT);
        // 현재 선택된 계좌 확인 (selectedAccount.value 또는 localStorage에서)
        // null을 undefined로 변환
        const currentAccount = selectedAccount.value || storedAccountNo || undefined;
        // 저장된 계좌가 목록에 있고 활성(visible === 'Y')인지 확인
        // 다른 클라이언트에서 비활성화한 경우 선택 해제 후 계좌 관련 데이터 초기화
        const accountExists = currentAccount
            ? list.some((account) => account.accountNo === currentAccount && account.visible === 'Y')
            : false;
        if (accountExists && currentAccount) {
            // 저장된 계좌가 목록에 있으면 해당 계좌 선택
            // selectAccount를 호출하여 토큰 확인/갱신
            await selectAccount(currentAccount);
        }
        else if (list.length > 0) {
            // 저장된 계좌가 없거나 목록에 없는 경우, 첫 번째 활성 계좌 선택
            // visible === 'Y'인 계좌만 필터링하고 visibleSequence가 가장 작은 계좌 찾기
            const activeAccounts = list.filter((account) => account.visible === 'Y');
            if (activeAccounts.length > 0) {
                const sortedList = [...activeAccounts].sort((a, b) => Number(a.visibleSequence) - Number(b.visibleSequence));
                await selectAccount(sortedList[0].accountNo);
            }
            else {
                // 활성 계좌가 없으면 선택하지 않음
                await selectAccount(undefined);
            }
        }
        else {
            // 계좌 목록이 비어있으면 선택된 계좌 초기화
            await selectAccount(undefined);
        }
    };
    const handleAccountTokenValidationFailure = async () => {
        try {
            const services = getServices();
            const response = await services.authService.getAccountJwtToken({
                accountNo: selectedAccountNoSafe.value,
            });
            if (response.data?.token) {
                setAccountToken(response.data.token);
            }
            else {
                console.error('[AccountStore] JWT 토큰 발급 실패: 응답에 토큰 없음');
            }
        }
        catch (error) {
            console.error('[AccountStore] JWT 토큰 발급 실패:', error);
        }
    };
    /**
     * 계좌 목록을 초기화합니다.
     * 로컬 스토리지의 선택 계좌 정보는 로그아웃 시에도 제거하지 않고 유지합니다.
     */
    const resetAccountList = () => {
        accountList.value = [];
        selectedAccount.value = undefined;
        clearAccountToken();
        // 초기화 상태는 유지 (이미 초기화된 상태이므로)
    };
    /**
     * localStorage에 저장된 계좌가 있으면 토큰을 자동으로 발급받습니다.
     * 비로그인 상태에서는 로컬스토리지의 계좌를 사용하지 않습니다.
     *
     * @returns Promise<void>
     */
    const initialize = async (isAccountAuthorized = false) => {
        try {
            const services = getServices();
            const accessToken = services.getLocalStorageItem(services.LocalStorageKey.ACCESS_TOKEN);
            // 비로그인 상태에서는 로컬스토리지의 계좌를 사용하지 않음
            if (!accessToken) {
                selectedAccount.value = undefined;
                clearAccountToken();
                _isInitialized.value = true; // 초기화 완료 (계좌 없음 상태)
                return;
            }
            const storedAccountNo = services.getLocalStorageItem(services.LocalStorageKey.SELECTED_ACCOUNT);
            if (storedAccountNo) {
                // localStorage에서 Account Token 로드 (선택된 계좌가 있을 때만)
                const storedAccountToken = services.getLocalStorageItem(services.LocalStorageKey.ACCOUNT_TOKEN);
                // 저장된 토큰이 있으면 먼저 설정 (API 호출 전에 빠른 초기화)
                if (storedAccountToken) {
                    accountToken.value = storedAccountToken;
                    const wsService = services.getWebSocketService();
                    wsService.setAccessToken(storedAccountToken);
                }
                // 저장된 계좌가 있으면 토큰 발급 (selectAccount가 API 호출하여 최신 토큰으로 갱신)
                await selectAccount(storedAccountNo, isAccountAuthorized);
            }
            _isInitialized.value = true; // 초기화 완료
        }
        catch (error) {
            console.error('[AccountStore] 초기화 중 오류:', error);
            _isInitialized.value = true; // 오류 발생해도 초기화 완료로 표시 (무한 대기 방지)
            throw error;
        }
    };
    // Computed getters for common usage patterns
    /**
     * 선택된 계좌번호
     * 계좌가 변경되면 자동으로 업데이트
     */
    const selectedAccountNo = computed(() => selectedAccount.value);
    /**
     * 계좌 비밀번호 실패 횟수
     */
    const accountPasswordFailedCount = computed(() => accountPasswordFailedCountData.value);
    /**
     * 선택된 계좌번호 - 문자열 기본값
     */
    const selectedAccountNoSafe = computed(() => selectedAccount.value || '');
    /**
     * 선택된 계좌번호가 있는지 확인
     */
    const hasSelectedAccount = computed(() => !!selectedAccount.value);
    /**
     * 계좌 목록
     */
    const accountListComputed = computed(() => accountList.value);
    /**
     * 활성 계좌 목록
     */
    const availableAccountListComputed = computed(() => accountList.value.filter((account) => account.visible === 'Y'));
    /**
     * 활성 정렬된 계좌 목록
     */
    const availableAccountListSortedComputed = computed(() => availableAccountListComputed.value.sort((a, b) => Number(a.visibleSequence) - Number(b.visibleSequence)));
    /**
     * 비활성 계좌 목록
     */
    const disabledAccountListComputed = computed(() => accountList.value.filter((account) => account.visible === 'N'));
    /**
     * 비활성 정렬된 계좌 목록
     */
    const disabledAccountListSortedComputed = computed(() => disabledAccountListComputed.value.sort((a, b) => Number(a.visibleSequence) - Number(b.visibleSequence)));
    /**
     * 계좌 간편인증 사용 여부
     */
    const accountSimpleAuthEnabledComputed = computed(() => accountSimpleAuthEnabled.value);
    /**
     * WebSocket용 액세스 토큰
     */
    /**
     * 계좌 인증용 JWT 토큰 (computed)
     * API 요청의 X-Account-Token 헤더 및 WebSocket private 채널 구독에 사용
     */
    const accountTokenComputed = computed(() => accountToken.value);
    /**
     * 계좌 인증 진행 상태
     * 계좌 인증 토큰 발급 중인지 여부를 나타냅니다.
     */
    const isAuthenticatingComputed = computed(() => isAuthenticating.value);
    /**
     * 계좌 인증 완료를 기다리는 Promise
     * 계좌 인증이 진행 중일 때 다른 API 요청이 대기할 수 있도록 합니다.
     */
    const authenticationPromiseComputed = computed(() => authenticationPromise);
    /**
     * 계좌 비밀번호 재설정 대상 계좌번호
     */
    const resetChangeAccountNo = computed(() => resetChangeAccountNoData.value);
    const setAccountPasswordFailedCount = (count) => {
        accountPasswordFailedCountData.value = count;
    };
    const resetAccountPasswordFailedCount = () => {
        accountPasswordFailedCountData.value = 0;
    };
    /**
     * 계좌 비밀번호 재설정 대상 계좌번호 설정
     * @param accountNo - 재설정 대상 계좌번호 (null이면 초기화)
     */
    const setResetChangeAccountNo = (accountNo) => {
        resetChangeAccountNoData.value = accountNo;
    };
    /**
     * 계좌 비밀번호 재설정 대상 계좌번호 초기화
     */
    const clearResetChangeAccountNo = () => {
        resetChangeAccountNoData.value = null;
    };
    /**
     * 계좌 간편인증 사용 여부 설정
     * @param enabled - 계좌 간편인증 사용 여부
     */
    const setAccountSimpleAuthEnabled = (enabled) => {
        const services = getServices();
        if (enabled) {
            services.removeLocalStorageItem(services.LocalStorageKey.ACCOUNT_PASSWORD_POPUP_NEEDED);
            services.setLocalStorageItem(services.LocalStorageKey.SAVE_ACCOUNT_PASSWORD_AGREED, true);
        }
        else {
            services.removeLocalStorageItem(services.LocalStorageKey.SAVE_ACCOUNT_PASSWORD_AGREED);
        }
        accountSimpleAuthEnabled.value = enabled;
    };
    return {
        // Actions
        selectAccount,
        setAccountList,
        resetAccountList,
        setAccountToken,
        clearAccountToken,
        handleAccountGradeChanged, // 계좌 등급 변경 처리, 웹소켓 재연결 시에도 사용
        initialize, // 앱 초기화 시 호출
        handleAccountTokenValidationFailure,
        // State
        accountList: accountListComputed,
        selectedAccount,
        isInitialized,
        // Computed getters
        selectedAccountNo,
        selectedAccountNoSafe,
        accountPasswordFailedCount,
        hasSelectedAccount,
        accountListComputed,
        accountTokenComputed,
        resetChangeAccountNo,
        isAuthenticating: isAuthenticatingComputed,
        authenticationPromise: authenticationPromiseComputed,
        accountSimpleAuthEnabledComputed,
        availableAccountListComputed,
        availableAccountListSortedComputed,
        disabledAccountListComputed,
        disabledAccountListSortedComputed,
        // Actions
        setAccountPasswordFailedCount,
        resetAccountPasswordFailedCount,
        setResetChangeAccountNo,
        clearResetChangeAccountNo,
        setAccountSimpleAuthEnabled,
    };
});
//# sourceMappingURL=useAccountStore.js.map